import { FC, useMemo, useState } from 'react';
import { useAppSelector } from '../../redux/hooks';
import { selectedAccessToken } from '../../redux/reducers/accessTokenSlice';

export interface AppearanceInput {
  pronoun: string;
  skinToneHex: string;
  hairColorHex: string;
  hairStyle: string;
  ethnicity?: string[];
}

export interface MonkSkinToneOption {
  mst: number;
  name: string;
  hex: string;
}

export const SKIN_TONE_MST_OPTIONS: MonkSkinToneOption[] = [
  { mst: 10, name: 'deepest brown', hex: '#292420' },
  { mst: 9, name: 'very deep brown', hex: '#3A312A' },
  { mst: 8, name: 'deep brown', hex: '#604134' },
  { mst: 7, name: 'medium-deep brown', hex: '#825C43' },
  { mst: 6, name: 'tan', hex: '#A07E56' },
  { mst: 5, name: 'medium', hex: '#D7BD96' },
  { mst: 4, name: 'light-medium', hex: '#EADABA' },
  { mst: 3, name: 'light warm', hex: '#F7EAD0' },
  { mst: 2, name: 'light', hex: '#F3E7DB' },
  { mst: 1, name: 'very light', hex: '#F6EDE4' },
];

export interface HairColorOption {
  name: string;
  hex: string;
}

export const HAIR_COLOR_OPTIONS: HairColorOption[] = [
  { name: 'Raven Black', hex: '#141314' },
  { name: 'Dark Brown', hex: '#4A3327' },
  { name: 'Ash Brown', hex: '#5A4A3F' },
  { name: 'Medium Brown', hex: '#644632' },
  { name: 'Light Brown', hex: '#7D5A3E' },
  { name: 'Dark Blonde', hex: '#96714B' },
  { name: 'Golden Blonde', hex: '#B6905A' },
  { name: 'Light Blonde', hex: '#D1B27A' },
  { name: 'Strawberry Blonde', hex: '#CB8B63' },
  { name: 'Platinum Blonde', hex: '#E9D9BC' },
  { name: 'Silver Gray', hex: '#A9ADB4' },
  { name: 'White', hex: '#F0F0EC' },
  { name: 'Cherry Red', hex: '#B2262D' },
  { name: 'Copper Orange', hex: '#C5672F' },
  { name: 'Rose Pink', hex: '#C65A8B' },
  { name: 'Lavender', hex: '#9A7AD9' },
  { name: 'Violet Purple', hex: '#7E4BC6' },
  { name: 'Electric Blue', hex: '#2D5DFF' },
  { name: 'Teal', hex: '#1AA7A3' },
  { name: 'Emerald Green', hex: '#12A86B' },
];

export const HAIR_STYLE_OPTIONS = [
  'Bald',
  'Buzz cut',
  'Crew cut',
  'Fade',
  'Undercut',
  'Pompadour',
  'Quiff',
  'Slicked back',
  'Short straight',
  'Short wavy',
  'Short curly',
  'Short coily',
  'Medium straight',
  'Medium wavy',
  'Medium curly',
  'Medium coily',
  'Long straight',
  'Long wavy',
  'Long curly',
  'Long coily',
  'Afro',
  'Twists',
  'Braids',
  'Cornrows',
  'Box braids',
  'Locs',
  'Bantu knots',
  'Pigtails',
  'Ponytail',
  'Bun',
  'Top knot',
  'Half-up half-down',
  'Bob',
  'Lob',
  'Pixie cut',
  'Shaved side',
  'Mullet',
  'Mohawk',
  'Turban',
  'Hijab',
  'Tichel',
  'Kufi cap',
  'Yarmulke',
];

export const ETHNICITY_OPTIONS = [
  'African',
  'African American',
  'Afro-Caribbean',
  'Arab',
  'Assyrian',
  'Berber/Amazigh',
  'Caribbean',
  'Central Asian',
  'East African',
  'East Asian',
  'Ethiopian',
  'European',
  'Filipino',
  'Hispanic/Latino',
  'Indigenous Australian',
  'Indigenous North American',
  'Inuit',
  'Jewish',
  'Korean',
  'Maori',
  'Mediterranean',
  'Melanesian',
  'Mestizo',
  'Middle Eastern',
  'Native American',
  'North African',
  'Pacific Islander',
  'Persian/Iranian',
  'Polynesian',
  'Roma',
  'Sami',
  'South Asian',
  'Southeast Asian',
  'Sub-Saharan African',
  'West African',
  'White/Caucasian',
  'Mixed',
  'Prefer not to say',
];

export const PRONOUN_OPTIONS = ['he/him', 'she/her', 'they/them'];

interface BaseAvatarInputPanelProps {
  pronoun: string;
  skinToneHex: string;
  hairColorHex: string;
  hairStyle: string;
  ethnicity: string[];
  onPronounChange: (value: string) => void;
  onSkinToneHexChange: (value: string) => void;
  onHairColorHexChange: (value: string) => void;
  onHairStyleChange: (value: string) => void;
  onEthnicityChange: (values: string[]) => void;
}

export const BaseAvatarInputPanel: FC<BaseAvatarInputPanelProps> = ({
  pronoun,
  skinToneHex,
  hairColorHex,
  hairStyle,
  ethnicity,
  onPronounChange,
  onSkinToneHexChange,
  onHairColorHexChange,
  onHairStyleChange,
  onEthnicityChange,
}) => {
  const handleEthnicityToggle = (option: string) => {
    if (ethnicity.includes(option)) {
      onEthnicityChange(ethnicity.filter(value => value !== option));
      return;
    }

    onEthnicityChange([...ethnicity, option]);
  };

  return (
    <div className="tw-flex tw-flex-col tw-gap-2 tw-pr-4">
      <h2 className="tw-text-xl tw-font-semibold tw-text-gray-900">Future Self Avatar Builder</h2>

      <label className="tw-flex tw-flex-col tw-gap-1">
        <span className="tw-text-sm tw-text-gray-800">Pronoun</span>
        <div className="tw-grid tw-grid-cols-3 tw-gap-1">
          {PRONOUN_OPTIONS.map(option => {
            const isSelected = pronoun === option;

            return (
              <button
                key={option}
                type="button"
                onClick={() => onPronounChange(option)}
                className={`tw-rounded-full tw-px-2 tw-py-1 tw-text-xs tw-border ${
                  isSelected
                    ? 'tw-bg-blue-600 tw-text-white tw-border-blue-600'
                    : 'tw-bg-white tw-text-gray-800 tw-border-gray-300 hover:tw-bg-gray-50'
                }`}
                aria-pressed={isSelected}
              >
                {option}
              </button>
            );
          })}
        </div>
      </label>

      <label className="tw-flex tw-flex-col tw-gap-1">
        <span className="tw-text-sm tw-text-gray-800">Skin tone</span>
        <div className="tw-grid tw-grid-cols-5 tw-gap-1 tw-rounded-lg tw-border tw-border-gray-300 tw-p-1.5">
          {SKIN_TONE_MST_OPTIONS.map(option => {
            const isSelected = skinToneHex === option.hex;

            return (
              <button
                key={option.name}
                type="button"
                onClick={() => onSkinToneHexChange(option.hex)}
                className={`tw-h-7 tw-w-7 tw-rounded-md tw-border-2 ${
                  isSelected ? 'tw-border-blue-600 tw-ring-2 tw-ring-blue-200' : 'tw-border-transparent'
                }`}
                style={{ backgroundColor: option.hex }}
                aria-label={`Select skin tone ${option.name} MST ${option.mst} swatch ${option.hex}`}
              />
            );
          })}
        </div>
      </label>

      <label className="tw-flex tw-flex-col tw-gap-1">
        <span className="tw-text-sm tw-text-gray-800">Hair color</span>
        <div className="tw-grid tw-grid-cols-5 tw-gap-1 tw-rounded-lg tw-border tw-border-gray-300 tw-p-1.5">
          {HAIR_COLOR_OPTIONS.map(option => {
            const isSelected = hairColorHex === option.hex;

            return (
              <button
                key={option.name}
                type="button"
                onClick={() => onHairColorHexChange(option.hex)}
                className={`tw-h-7 tw-w-7 tw-rounded-md tw-border-2 ${
                  isSelected ? 'tw-border-blue-600 tw-ring-2 tw-ring-blue-200' : 'tw-border-transparent'
                }`}
                style={{ backgroundColor: option.hex }}
                aria-label={`Select hair color ${option.name} swatch ${option.hex}`}
              />
            );
          })}
        </div>
      </label>

      <label className="tw-flex tw-flex-col tw-gap-1">
        <span className="tw-text-sm tw-text-gray-800">Hair style</span>
        <select
          value={hairStyle}
          onChange={e => onHairStyleChange(e.target.value)}
          className="tw-border tw-border-gray-300 tw-rounded-lg tw-p-1.5 tw-text-sm"
        >
          <option value="">Select hairstyle...</option>
          {HAIR_STYLE_OPTIONS.map(option => (
            <option key={option} value={option}>
              {option}
            </option>
          ))}
        </select>
      </label>

      <div className="tw-flex tw-flex-col tw-gap-1">
        <span className="tw-text-sm tw-text-gray-800">Ethnicity (optional)</span>
        <div className="tw-max-h-32 tw-overflow-y-auto tw-rounded-lg tw-border tw-border-gray-300 tw-p-1.5 tw-space-y-1">
          {ETHNICITY_OPTIONS.map(option => (
            <label key={option} className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-gray-800">
              <input
                type="checkbox"
                checked={ethnicity.includes(option)}
                onChange={() => handleEthnicityToggle(option)}
                className="tw-h-4 tw-w-4"
              />
              <span>{option}</span>
            </label>
          ))}
        </div>
      </div>
    </div>
  );
};

interface BaseAvatarDisplayPanelProps {
  imageUrl: string;
  isGenerating: boolean;
  error: string;
  canGenerate: boolean;
  hasGeneratedSuccessfully: boolean;
  onGenerate: () => void;
  onContinue?: () => void;
}

export const BaseAvatarDisplayPanel: FC<BaseAvatarDisplayPanelProps> = ({
  imageUrl,
  isGenerating,
  error,
  canGenerate,
  hasGeneratedSuccessfully,
  onGenerate,
  onContinue,
}) => {
  return (
    <div className="tw-flex tw-flex-col tw-items-center tw-justify-start tw-gap-4 tw-min-h-0">
      <div className="tw-w-full tw-max-w-[512px] tw-aspect-square tw-max-h-[52vh] tw-border tw-border-dashed tw-border-gray-300 tw-rounded-2xl tw-bg-gray-50 tw-flex tw-items-center tw-justify-center tw-overflow-hidden">
        {isGenerating && (
          <div className="tw-text-center">
            <div className="tw-text-sm tw-text-gray-600">Generating avatar...</div>
          </div>
        )}
        {imageUrl && !isGenerating && (
          <img src={imageUrl} alt="Generated avatar" className="tw-w-full tw-h-full tw-object-cover tw-rounded-2xl" />
        )}
        {error && (
          <div className="tw-text-center tw-text-red-600 tw-text-sm tw-px-4">
            <div>{error}</div>
          </div>
        )}
      </div>
      {!hasGeneratedSuccessfully && (
        <button
          type="button"
          onClick={onGenerate}
          disabled={!canGenerate || isGenerating}
          className="tw-rounded-full tw-px-6 tw-py-2 tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700 disabled:tw-bg-gray-300 disabled:tw-text-gray-500 disabled:tw-cursor-not-allowed"
        >
          {isGenerating ? 'Generating...' : 'Generate Avatar'}
        </button>
      )}
      {hasGeneratedSuccessfully && onContinue && (
        <button
          type="button"
          onClick={onContinue}
          className="tw-rounded-full tw-px-6 tw-py-2 tw-bg-blue-600 tw-text-white hover:tw-bg-blue-700"
        >
          Continue to Personality
        </button>
      )}
    </div>
  );
};

interface BaseAvatarPanelProps {
  onAvatarGenerated?: (imageUrl: string, appearance: AppearanceInput) => void;
  onContinue?: () => void;
}

export const BaseAvatarPanel: FC<BaseAvatarPanelProps> = ({ onAvatarGenerated, onContinue }) => {
  const [pronoun, setPronoun] = useState('');
  const [skinToneHex, setSkinToneHex] = useState('');
  const [hairColorHex, setHairColorHex] = useState('');
  const [hairStyle, setHairStyle] = useState('');
  const [ethnicity, setEthnicity] = useState<string[]>([]);
  const [imageUrl, setImageUrl] = useState<string>('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState('');
  const [hasGeneratedSuccessfully, setHasGeneratedSuccessfully] = useState(false);

  const accessToken = useAppSelector(selectedAccessToken);
  const appearance = useMemo(
    () => ({ pronoun, skinToneHex, hairColorHex, hairStyle, ethnicity }),
    [pronoun, skinToneHex, hairColorHex, hairStyle, ethnicity],
  );

  const canGenerate = Boolean(pronoun && skinToneHex && hairColorHex && hairStyle);

  const handleGenerateAvatar = async () => {
    if (!canGenerate || hasGeneratedSuccessfully) {
      return;
    }

    setIsGenerating(true);
    setError('');
    setImageUrl('');

    try {
      const url = `${import.meta.env.VITE_BFF_URL}/genai/teame223/avatar`;

      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(accessToken && { Authorization: `Bearer ${accessToken}` }),
        },
        body: JSON.stringify({ appearance }),
      });

      if (!response.ok) {
        throw new Error(`Failed to generate image: ${response.status}`);
      }

      const blob = await response.blob();
      const imageObjectUrl = URL.createObjectURL(blob);
      setImageUrl(imageObjectUrl);
      setHasGeneratedSuccessfully(true);
      onAvatarGenerated?.(imageObjectUrl, appearance);
    } catch (err) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to generate avatar';
      setError(errorMsg);
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="tw-flex-1 tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-6 tw-p-6 lg:tw-p-8 tw-pb-4 tw-min-h-0 tw-overflow-y-auto">
      <BaseAvatarInputPanel
        pronoun={pronoun}
        skinToneHex={skinToneHex}
        hairColorHex={hairColorHex}
        hairStyle={hairStyle}
        ethnicity={ethnicity}
        onPronounChange={setPronoun}
        onSkinToneHexChange={setSkinToneHex}
        onHairColorHexChange={setHairColorHex}
        onHairStyleChange={setHairStyle}
        onEthnicityChange={setEthnicity}
      />

      <BaseAvatarDisplayPanel
        imageUrl={imageUrl}
        isGenerating={isGenerating}
        error={error}
        canGenerate={canGenerate}
        hasGeneratedSuccessfully={hasGeneratedSuccessfully}
        onGenerate={handleGenerateAvatar}
        onContinue={onContinue ? () => onContinue() : undefined}
      />
    </div>
  );
};

export default BaseAvatarPanel;
